import React from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import {
    PaymentPaypal,
    getDisplayPayPalButtonsFlags,
    getPayPalButtonDisplayProps,
    getPaymentTypeInitialValue,
} from "@qgiv/core-donor";
import { constants } from "@qgiv/core-js";
import { useFormikContext } from "formik";
import { selectIsCms } from "../../../redux/slices/configSlice";
import {
    incrementPaymentMethodResetCount,
    selectPayPal,
    setHasCompletedPayPalAuthorizationFlow,
    setHasInitiatedPayPalAuthorizationFlow,
    setPayPalPaymentSource,
} from "../../../redux/slices/appSettingsSlice";
import { selectAllFormSettings } from "../../../redux/slices/formSettingsSlice";
import {
    selectAllRegistration,
    selectRegistrationTotals,
} from "../../../redux/slices/registrationSlice";
import { selectCmsControlByIdByPage } from "../../../redux/slices/cmsSettingsSlice";

const ConnectedPaymentPaypal = ({ controlId, paymentData = {} }) => {
    const {
        ENUMS: { DefaultView, PaymentType },
    } = constants;

    const isCms = useSelector(selectIsCms);
    const payPal = useSelector(selectPayPal);
    const {
        hasCompletedPayPalAuthorizationFlow = false,
        hasInitiatedPayPalAuthorizationFlow = false,
        paymentMethodResetCount = 0,
        payPalPaymentSource = "paypal",
    } = payPal;
    // Extract the data that is relevant to the PayPal buttons
    const { path, payPalSDK } = useSelector(selectAllFormSettings);
    const registration = useSelector(selectAllRegistration);
    const { total = 0 } = useSelector(selectRegistrationTotals);
    const control = useSelector((state) =>
        selectCmsControlByIdByPage(
            controlId,
            DefaultView.REGISTRATION_CHECKOUT_STEP,
            state,
        ),
    );
    const {
        options: {
            paypal: {
                authenticatedMessage = "You're all set! If your total changes, we'll ask you to authenticate with PayPal again.",
                defaultMessage = "You'll be redirected to your PayPal account, then returned here to complete your transaction.",
            },
        },
    } = control;

    // Boolean that determines which version of PayPal to display
    // Re-render if there is any change to the overall total of the
    // registration. Without this, changes to the registration will not be
    // passed along in the createOrder() request that is sent to the BE to
    // initiate the transaction
    const forceReRender = [total];
    // Only enable the PayPal button when there is a donation amount to process
    const hasAnAmountToSendToPayPal = total > 0;
    // P2P does not have any recurring donations that should trigger the user
    // of a PayPal billing agreement
    const isRecurringDonation = false;
    const platformSpecificDataForCreateOrderAPI = {
        payPalSDK,
        registration,
    };
    // Get the flags that determine whether the PayPal buttons should be
    // displayed
    const {
        displayPayPalSDKScriptProvider,
        shouldLoadPayPalSDKScriptProvider,
    } = getDisplayPayPalButtonsFlags(payPalSDK);
    // Arrange all of the different props that we need to pass to the
    // PayPal SDK Buttons
    const payPalButtonDisplayPropsOptions = {
        displayPayPalSDKScriptProvider,
        forceReRender,
        hasAnAmountToSendToPayPal,
        isCms,
        isRecurringDonation,
        payPalSDK,
        platformSpecificDataForCreateOrderAPI,
        paymentMethodResetCount,
        shouldLoadPayPalSDKScriptProvider,
    };
    // Arrange all of the different props that we need to pass to the
    // PayPal SDK Buttons
    const payPalButtonDisplayProps = getPayPalButtonDisplayProps(
        payPalButtonDisplayPropsOptions,
    );

    // P2P does not support any recurring donations
    const isRecurring = false;

    // Dispatch Logic
    const dispatch = useDispatch();

    const { initialValues = {}, setFieldValue = () => {} } = useFormikContext();
    const emptyDonorAccount = {};
    // Get the Payment_Type value that is set once the user has selected a new
    // payment method as opposed to a stored payment method
    const initialPaymentTypeForNewPaymentMethods = getPaymentTypeInitialValue(
        paymentData,
        emptyDonorAccount,
    );

    // Handle all of the platform and and location specific UI updates that
    // affect application state after completing the PayPal SDK request. In
    // this instance that involves (1) Setting all of the hidden PayPal values
    // (2) Updating the Redux store.
    const handleCompletePayPalAuthorizationFlow = ({
        payPalValues = {},
        // eslint-disable-next-line no-shadow
        payPalPaymentSource = "paypal",
    }) => {
        const {
            PayPal_Agreement_ID = "",
            PayPal_Is_Venmo = false,
            PayPal_Payer_ID = "",
            PayPal_Token = "",
        } = payPalValues;

        // Set the value for the relevant PayPal fields including the billing
        // agreement ID which is required for recurring donations
        setFieldValue("Payment_Type", PaymentType.PAYPAL);
        setFieldValue("PayPal_Payer_ID", PayPal_Payer_ID);
        setFieldValue("PayPal_Token", PayPal_Token);

        if (PayPal_Agreement_ID) {
            setFieldValue("PayPal_Agreement_ID", PayPal_Agreement_ID);
        }

        if (PayPal_Is_Venmo) {
            setFieldValue("PayPal_Is_Venmo", true);
        }

        // Update the relevant values in the Redux store. Incrementing the step
        // submit count matches the behavior on the continue button and enables
        // an error message to be displayed if there is one
        dispatch(setHasCompletedPayPalAuthorizationFlow(true));
        dispatch(setPayPalPaymentSource(payPalPaymentSource));
    };

    // Display the use a different payment method button
    const handleInitiatePayPalAuthorizationFlow = (
        updatedHasInitiatedPayPalAuthorizationFlow,
    ) => {
        dispatch(
            setHasInitiatedPayPalAuthorizationFlow(
                updatedHasInitiatedPayPalAuthorizationFlow,
            ),
        );
    };

    // Wipe all of the pre-existing PayPal values & replace them with the
    // initial versions of these values while also updating any PayPal related
    // data in the Redux store.In this instance we cannot use the payment type
    // from the initial value as object as we need the payment type that is
    // initially set when the user view the new payment method UI.
    const handleResetPayPalAuthorizationFlow = () => {
        setFieldValue("Payment_Type", initialPaymentTypeForNewPaymentMethods);
        setFieldValue("PayPal_Agreement_ID", initialValues.PayPal_Agreement_ID);
        setFieldValue("PayPal_Is_Venmo", initialValues.PayPal_Is_Venmo);
        setFieldValue("PayPal_Payer_ID", initialValues.PayPal_Payer_ID);
        setFieldValue("PayPal_Token", initialValues.PayPal_Token);

        dispatch(setHasInitiatedPayPalAuthorizationFlow(false));
        dispatch(setHasCompletedPayPalAuthorizationFlow(false));
        dispatch(setPayPalPaymentSource("paypal"));
    };

    // If you increment the payment reset count all of the reset paypal
    // authorization logic will run
    const handleLegacyPayPalReset = () => {
        dispatch(incrementPaymentMethodResetCount());
    };

    const paymentPaypalProps = {
        authenticationAmount: total,
        authenticatedMessage,
        defaultMessage,
        hasInitiatedPayPalAuthorizationFlow,
        hasCompletedPayPalAuthorizationFlow,
        isRecurring,
        path,
        paymentMethodResetCount,
        payPalButtonDisplayProps,
        payPalPaymentSource,
        handleCompletePayPalAuthorizationFlow,
        handleInitiatePayPalAuthorizationFlow,
        handleLegacyPayPalReset,
        handleResetPayPalAuthorizationFlow,
    };
    return <PaymentPaypal {...paymentPaypalProps} />;
};

ConnectedPaymentPaypal.propTypes = {
    controlId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired,
    paymentData: PropTypes.shape({}).isRequired,
};

export default ConnectedPaymentPaypal;
